<template>
  <modal-template :id="id" :name="name" :showModal="showModal" v-on:hide-modal="close()">
    <template slot="modalContent">
      <v-tabs v-model="activeTab" grow>
        <v-tab key="data" ripple>Data</v-tab>
        <v-tab key="preferences" ripple>Preference Parameters</v-tab>
        <v-tab-item key="data">
          <v-tabs v-model="activeTabData" grow color="info" dark>
            <v-tab key="criteria" ripple>Criteria</v-tab>
            <v-tab key="actions" ripple>Actions</v-tab>
            <v-tab key="performanceTable" ripple>Performance Table</v-tab>

            <v-tab-item key="criteria">
              <div id="subheader">
                <v-data-table id="criteria-table" :items="criteria" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left" style="width: 25%">Description</th>
                    <th class="text-xs-left column-wrap">Direction</th>
                    <th class="text-xs-left column-wrap">Scale Type</th>
                    <th class="text-xs-left">Min</th>
                    <th class="text-xs-left">Max</th>
                    <th class="text-xs-left column-wrap">Num Levels</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Name']" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-textarea placeholder="Insert value" v-model="props.item['Description']" hide-details rows="1" maxlength="150"></v-textarea>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="['Maximization', 'Minimization']" v-model="props.item['Direction']" hide-details>
                        {{ props.item['Direction'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="['Cardinal', 'Ordinal']" v-model="props.item['Scale Type']" hide-details>
                        {{ props.item['Scale Type'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-text-field v-if="props.item['Scale Type']=='Cardinal'" placeholder="Insert value" v-model="props.item['Min']"
                                    type="number" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-if="props.item['Scale Type']=='Cardinal'" placeholder="Insert value" v-model="props.item['Max']"
                                    type="number" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-text-field v-if="props.item['Scale Type']=='Ordinal'" placeholder="Insert value" v-model="props.item['Num Levels']"
                                    type="number" :rules="[rules.positiveInt]" validate-on-blur hide-details></v-text-field>
                    </td>
                    <td>
                      <v-btn icon @click="removeCriteriaLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addCriteriaLine(null)" color="info">New Criterion</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="actions">
              <div id="subheader">
                <v-data-table id="actions-table" :items="actions" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left">Description</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Name']" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-textarea placeholder="Insert value" v-model="props.item['Description']" hide-details rows="1" maxlength="150"></v-textarea>
                    </td>
                    <td>
                      <v-btn icon @click="removeActionsLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addActionsLine(null)" color="info">New Action</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="performanceTable">
              <div id="subheader">
                <v-data-table id="performance-table" :items="performanceTable" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td class="text-xs-left">{{ props.item['Name'] }}</td>
                    <td v-for="crit in criteria" :key="crit['Name']">
                      <v-tooltip top>
                        <v-text-field slot="activator" placeholder="Insert value" v-model="props.item[crit['Name']]" type="number" hide-details
                                      :rules="[rules[crit['Name']]]" validate-on-blur @change="checkIfDirty('performanceTable')"></v-text-field>
                        <span v-if="crit['Scale Type']==='Cardinal'">Min: {{ crit['Min'] }}, Max: {{ crit['Max'] }}</span>
                        <span v-else-if="crit['Scale Type']==='Ordinal'">Num Levels: {{ crit['Num Levels'] }}</span>
                      </v-tooltip>
                    </td>
                  </template>
                </v-data-table>
              </div>
            </v-tab-item>
          </v-tabs>
        </v-tab-item>

        <v-tab-item key="preferences">
          <v-tabs v-model="activeTabPreferences" color="info" grow dark>
            <v-tab key="referenceActions" ripple>Reference Actions</v-tab>
            <v-tab key="functions" ripple>SD Functions</v-tab>
            <v-tab key="weights" ripple>Weights</v-tab>
            <v-tab key="interactionCoefficients" ripple>Interaction Coefficients</v-tab>
            <v-tab key="likenessThresholds" ripple>Likeness Thresholds</v-tab>

            <v-tab-item key="referenceActions">
              <div id="subheader">
                <v-data-table id="ref-actions-table" :items="referenceActions" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Category</th>
                    <th class="text-xs-left" style="width: 20%">Name</th>
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-combobox placeholder="Select or create new" v-model="props.item['Category']" :items="weights.map(a => a['Category'])"
                                  hide-details></v-combobox>
                    </td>
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Name']" hide-details></v-text-field>
                    </td>
                    <td v-for="crit in criteria" :key="crit['Name']">
                      <v-tooltip top>
                        <v-text-field slot="activator" placeholder="Insert value" v-model="props.item[crit['Name']]" type="number" hide-details
                                      :rules="[rules[crit['Name']]]" validate-on-blur></v-text-field>
                        <span v-if="crit['Scale Type']==='Cardinal'">Min: {{ crit['Min'] }}, Max: {{ crit['Max'] }}</span>
                        <span v-else-if="crit['Scale Type']==='Ordinal'">Num Levels: {{ crit['Num Levels'] }}</span>
                      </v-tooltip>
                    </td>
                    <td>
                      <v-btn icon @click="removeReferenceActionsLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addReferenceActionsLine(null)" color="info">New Reference Action</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="functions">
              <div id="subheader">
                <v-data-table id="functions-table" :items="functions" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Criterion</th>
                    <th class="text-xs-left">Value</th>
                    <th class="text-xs-left">Condition</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-select placeholder="Please select" :items="criteria.map(a => a['Name'])" v-model="props.item['Criterion']" hide-details>
                        {{ props.item['Criterion'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Value']" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Condition']" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-btn icon @click="removeFunctionsLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addFunctionsLine()" color="info">New Function</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="weights">
              <div id="subheader">
                <v-data-table id="weights-table" :items="weights" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Category</th>
                    <th class="text-xs-left" v-for="crit in criteria" :key="crit['Name']">{{ crit['Name'] }}</th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td class="text-xs-left">{{ props.item['Category'] }}</td>
                    <td v-for="crit in criteria" :key="crit['Name']">
                      <v-text-field placeholder="Insert value" v-model="props.item[crit['Name']]" type="number" hide-details
                                    @change="checkIfDirty('weights')"></v-text-field>
                    </td>
                  </template>
                </v-data-table>
              </div>
            </v-tab-item>

            <v-tab-item key="interactionCoefficients">
              <div id="subheader">
                <v-data-table id="interaction-coefficients-table" :items="interactionCoefficients" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Category</th>
                    <th class="text-xs-left">Type</th>
                    <th class="text-xs-left">Criterion1</th>
                    <th class="text-xs-left">Criterion2</th>
                    <th class="text-xs-left">Value</th>
                    <th style="width: 70px;"></th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td>
                      <v-select placeholder="Please select" :items="weights.map(a => a['Category'])" v-model="props.item['Category']" hide-details>
                        {{ props.item['Category'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="typeInteractionCoefficients" v-model="props.item['Type']" hide-details>
                        {{ props.item['Type'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="criteria.map(a => a['Name'])" v-model="props.item['Criterion1']" hide-details>
                        {{ props.item['Criterion1'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-select placeholder="Please select" :items="criteria.map(a => a['Name'])" v-model="props.item['Criterion2']" hide-details>
                        {{ props.item['Criterion2'] }}
                      </v-select>
                    </td>
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Value']" type="number" hide-details></v-text-field>
                    </td>
                    <td>
                      <v-btn icon @click="removeInteractionCoefficientsLine(props.item)"><v-icon color="pink">delete</v-icon></v-btn>
                    </td>
                  </template>
                </v-data-table>
                <v-btn @click="addInteractionCoefficientsLine()" color="info">New Interaction Coefficient</v-btn>
              </div>
            </v-tab-item>

            <v-tab-item key="likenessThresholds">
              <div id="subheader">
                <v-data-table id="likeness-thresholds-table" :items="likenessThresholds" hide-actions class="elevation-5">
                  <template slot="no-data"><tr><td colspan="100%" class="text-xs-center">Empty</td></tr></template>
                  <template slot="headers" slot-scope="props">
                    <th class="text-xs-left" style="width: 20%">Category</th>
                    <th class="text-xs-left">Value</th>
                  </template>
                  <template slot="items" slot-scope="props">
                    <td class="text-xs-left">{{ props.item['Category'] }}</td>
                    <td>
                      <v-text-field placeholder="Insert value" v-model="props.item['Value']" type="number" :rules="[rules.likeness]"
                                    validate-on-blur hide-details @change="checkIfDirty('likenessThresholds')"></v-text-field>
                    </td>
                  </template>
                </v-data-table>
              </div>
            </v-tab-item>

          </v-tabs>
        </v-tab-item>
      </v-tabs>
    </template>
  </modal-template>
</template>

<script>
import {EventBus} from '@/event-bus.js'
import ModalTemplate from '@/components/Workspace/Methods/ModalTemplate'

export default {
  components: {
    ModalTemplate,
  },
  data: () => ({
    showModal: false,
    activeTab: null,
    activeTabData: null,
    activeTabPreferences: null,
    tabsData: ['criteria', 'actions', 'performanceTable'],
    tabsPreferences: ['referenceActions', 'functions', 'weights', 'interactionCoefficients', 'likenessThresholds'],

    criteria: [],
    functions: [],
    actions: [],
    performanceTable: [],
    weights: [],
    referenceActions: [],
    interactionCoefficients: [],
    likenessThresholds: [],
    
    dirty: {},
    dataFromConnection: false,
    cleaningUp: false,
    createNewCategory: false,
    newCategory: '',
    typeInteractionCoefficients: ['Mutual-Strengthening', 'Mutual-Weakening', 'Antagonistic'],

    rules: {
      required: value => !!value || this.a.methods.notify('Required'),
      positiveInt: value => /^\+?(0|[1-9]\d*)$/.test(value) || value==='' || this.a.methods.notify('Must be a positive integer'),
      sdValue: value => parseFloat(value)>=-1 && parseFloat(value)<=1 || value==='' || this.a.methods.notify('Must be a value between -1 and 1'),
      likeness: value => parseFloat(value)>=0.5 && parseFloat(value)<=1 || value==='' || this.a.methods.notify('Must be a value between 0.5 and 1')
    }
  }),
  props: {
    id: String,
    name: String,
  },
  methods: {
    notify(msg) {
      EventBus.$emit('send-notification', {text: msg});
      return false;
    },
    clone(object) {
      return JSON.parse(JSON.stringify(object))
    },
    validateData() {
      var errors = {};
      // performance table
      var actionNames = this.actions.map(c => c['Name']);
      for(var action of this.performanceTable) {
        if(!actionNames.includes(action['Name'])) {
          errors.performanceTable = 'actions referenced in Performance Table missing';
        }
      }
      // functions
      var criteriaNames = this.criteria.map(c => c['Name']);
      for(var func of this.functions) {
        if(!criteriaNames.includes(func['Criterion'])) {
          errors.functions = 'criteria referenced in SD Functions missing';
        }
      }
      // weights
      var categoryNames = this.referenceActions.map(a => a['Category']);
      for(var weight of this.weights) {
        if(!categoryNames.includes(weight['Category'])) {
          errors.weights = 'categories referenced in Weights missing';
        }
      }
      // likeness thresholds
      for(var likeness of this.likenessThresholds) {
        if(!categoryNames.includes(likeness['Category'])) {
          errors.likenessThresholds = 'categories referenced in Likeness Thresholds missing';
        }
      }
      // interaction coeffs
      var criteriaNames = this.criteria.map(c => c['Name']);
      var categoryNames = this.referenceActions.map(a => a['Category']);
      for(var coeff of this.interactionCoefficients) {
        if(!criteriaNames.includes(coeff['Criterion1']))
          errors.interactionCoefficients = 'criteria referenced in Interaction Coefficients missing';
        if(!criteriaNames.includes(coeff['Criterion2']))
          errors.interactionCoefficients = 'criteria referenced in Interaction Coefficients missing';
        if(!categoryNames.includes(coeff['Category']))
          errors.interactionCoefficients = 'categories referenced in Interaction Coefficients missing';
      }
      for(var param of this.tabsData.concat(this.tabsPreferences)) {
        if(errors[param]) {
          this.notify(this.name + ': ' + errors[param]);
          EventBus.$emit('set-endpoint-mode', {endpoint: this.id+'-'+param, mode: 'error'});
        } else if(!(typeof this[param]!=='undefined' && this[param].length===0))
          EventBus.$emit('set-endpoint-mode', {endpoint: this.id+'-'+param, mode: 'active'});
      }
    },
    saveModuleData() {
      var headers = ['Action'];
      this.weights.forEach(function(cat) {
        headers.push(cat['Category']);
      });
      headers.push('Non-assigned');
      var methodAttributes = {};
      methodAttributes.input = {
        criteria: this.criteria,
        actions: this.actions,
        performanceTable: this.performanceTable,
        functions: this.functions,
        referenceActions: this.referenceActions,
        weights: this.weights,
        likenessThresholds: this.likenessThresholds,
        interactionCoefficients: this.interactionCoefficients
      };
      methodAttributes.output = {
        headers: headers,
        description: "Assignment Results"
      };
      methodAttributes.id = this.id;
      this.validateData();
      EventBus.$emit('save-module-data', methodAttributes);
    },
    setPropertyFromInputFile(mod) {
      if(mod.newConnection) this.dataFromConnection = true;
      if(typeof mod.value!=='undefined' && mod.value.length===0) this.cleaningUp = true;
      if(['criteria', 'actions', 'referenceActions'].includes(mod.property)) {
        var val = this.clone(mod.value);
        while(this[mod.property].length>0)
          this['remove' + mod.property.charAt(0).toUpperCase() + mod.property.slice(1) + 'Line'](this[mod.property][0]);
        for(var element of val)
          this['add' + mod.property.charAt(0).toUpperCase() + mod.property.slice(1) + 'Line'](element);
      } else
        this[mod.property] = this.clone(mod.value);
      if(this.cleaningUp) this.dirty[mod.property] = false;
      else this.dirty[mod.property] = true;
      this.cleaningUp = false;
      this.saveModuleData();
      if(mod.newConnection) this.dataFromConnection = false;
    },
    checkIfDirty(prop) {
      if(!this.dirty[prop]) {
        this.dirty[prop] = true;
        var offset = this.tabsData.indexOf(prop)!==-1 ? this.tabsData.indexOf(prop) : 3+this.tabsPreferences.indexOf(prop);
        var obj = {target: this.id, property: prop, value: this[prop], yOffset: offset};
        EventBus.$emit('generate-data-module', obj);
        return true;
      } else return false;
    },
    addCriteriaLine(line) {
      var newCrit = line ? line : {'Name': '', 'Description': '', 'Direction': 'Maximization', 'Scale Type': 'Cardinal', 'Min': '', 'Max': '', 'Num Levels': ''};
      this.criteria.push(newCrit);
      this.rules[newCrit['Name']] = (value) => {
        if(value === '')
          return true;
        else if(newCrit['Scale Type']==='Cardinal') {
          var val = parseFloat(value);
          if(newCrit['Min'].length===0 || newCrit['Max'].length===0)
            return true;
          else if(val < newCrit['Min'] || val > newCrit['Max'])
            return this.notify('Must be a value between ' + newCrit['Min'] + ' and ' + newCrit['Max']);
        } else if(newCrit['Scale Type']==='Ordinal') {
          var val = parseInt(value);
          if(newCrit['Num Levels'].length===0)
            return true;
          else if(val < 1 || val > newCrit['Num Levels'] || !Number.isInteger(val)) {
            return this.notify('Must be a positive integer less than ' + newCrit['Num Levels']);}
        }
        return true;
      };
      // add watcher to check for changes in name, to update functions accordingly
      newCrit.watcher = this.$watch(
        function () {
          if(this.criteria.indexOf(newCrit)!==-1)
            return this.criteria[this.criteria.indexOf(newCrit)]['Name'];
          else
            return null;
        }, function (newName, oldName) {
          if(newName===null) {
            newCrit.watcher();
            return;
          }
          var funcs = this.functions.filter(function(e) {return e['Criterion'] === oldName;})
          for(var f of funcs) f['Criterion'] = newName;
      });
      if(!this.dataFromConnection) this.checkIfDirty('criteria');
    },
    removeCriteriaLine(line) {
      var index = this.criteria.indexOf(line);
      if(index > -1) {
        this.criteria.splice(index, 1);
        if(!this.cleaningUp) {
          this.functions = this.functions.filter(function( obj ) {
            return obj['Criterion'] !== line['Name'];
          });
        }
        line.watcher(); // destroy watcher
        delete this.rules[line['Name']];
      }
    },
    addActionsLine(item) {
      var newAction = item ? item : {'Name': '', 'Description': ''};
      this.actions.push(newAction);
      newAction.watcher = this.$watch(
        function () {
          if(this.actions.indexOf(newAction)!==-1)
            return this.actions[this.actions.indexOf(newAction)]['Name'];
          else
            return null;
        }, function(newName, oldName) {
          if(newName===null) {
            newAction.watcher();
            /*var performance = this.performanceTable.find(function(p) {return p['Name'] === oldName;})
            var index = this.performanceTable.indexOf(performance);
            if(index > -1) this.performanceTable.splice(index, 1);*/
            return;
          }
          var performance = this.performanceTable.find(function(p) {return p['Name'] === oldName;})
          performance['Name'] = newName;
      });
      if(!this.performanceTable.find(p => {return p['Name'] === newAction['Name']})) {
        var newPerformance = {'Name': newAction['Name']};
        this.performanceTable.push(newPerformance);
      }
      if(!this.dataFromConnection) this.checkIfDirty('actions')
    },
    removeActionsLine(line) {
      var index = this.actions.indexOf(line);
      if(index > -1) this.actions.splice(index, 1);
      if(!this.cleaningUp) {
        var performance = this.performanceTable.find(function(p) {return p['Name'] === line['Name'];})
        var index = this.performanceTable.indexOf(performance);
        if(index > -1) this.performanceTable.splice(index, 1);
      }
    },
    addFunctionsLine() {
      var newFunction = {'Criterion': '', 'Value': '', 'Condition': ''};
      this.functions.push(newFunction);
      if(!this.dataFromConnection) this.checkIfDirty('functions');
    },
    removeFunctionsLine(line) {
      var index = this.functions.indexOf(line);
      if(index > -1) this.functions.splice(index, 1);
    },
    addReferenceActionsLine(item) {
      var newRef = item ? item : {'Category': '', 'Name': ''};
      this.referenceActions.push(newRef);
      newRef.watcher = this.$watch(
        function () {
          if(this.referenceActions.indexOf(newRef)!==-1)
            return this.referenceActions[this.referenceActions.indexOf(newRef)]['Category'];
          else
            return null;
        }, function(newCategory, oldCategory) {
          // old category is gone, delete it everywhere
          if(!this.referenceActions.map(a => a['Category']).includes(oldCategory)) {
            if(this.referenceActions.length>0 || (this.referenceActions.length===0 && this.dirty.referenceActions)) {
              var weight = this.weights.find(w => {return w['Category'] === oldCategory})
              var index = this.weights.indexOf(weight);
              if(index > -1) this.weights.splice(index, 1);
              var likeness = this.likenessThresholds.find(t => {return t['Category'] === oldCategory})
              var index = this.likenessThresholds.indexOf(likeness);
              if(index > -1) this.likenessThresholds.splice(index, 1);
            }
          }
          // ref action was deleted
          if(newCategory===null) {
            newRef.watcher();
            return;
          }
          // propagate new category
          if(!this.weights.map(a => a['Category']).includes(newCategory)) {
            var newWeight = {'Category': newCategory};
            this.weights.push(newWeight);
          }
          if(!this.likenessThresholds.map(a => a['Category']).includes(newCategory)) {
            var newLikeness = {'Category': newCategory, 'Value': ''};
            this.likenessThresholds.push(newLikeness);
          }
      });
      if(item && item['Category']!=='' && !this.weights.map(a => a['Category']).includes(item['Category'])) {
        var newWeight = {'Category': item['Category']};
        this.weights.push(newWeight);
      }
      if(item && item['Category']!=='' && !this.likenessThresholds.map(a => a['Category']).includes(item['Category'])) {
        var newLikeness = {'Category': item['Category'], 'Value': ''};
        this.likenessThresholds.push(newLikeness);
      }
      if(!this.dataFromConnection) this.checkIfDirty('referenceActions');
    },
    removeReferenceActionsLine(line, categoryName) {
      var index = this.referenceActions.indexOf(line);
      if(index > -1) this.referenceActions.splice(index, 1);
    },
    addInteractionCoefficientsLine(line) {
      var newInt = {'Type': 'Mutual-Strengthening'};
      this.interactionCoefficients.push(newInt);
      if(!this.dataFromConnection) this.checkIfDirty('interactionCoefficients');
    },
    removeInteractionCoefficientsLine(line) {
      var index = this.interactionCoefficients.indexOf(line);
      if(index > -1) this.interactionCoefficients.splice(index, 1);
    },
    close () {
      this.saveModuleData();
      this.showModal=false;
    }
  },
  mounted: function () {
    EventBus.$on('show-modal-'+this.id, tab => {
      this.showModal = true;
      var tabId = this.tabsData.indexOf(tab);
      if(tabId!==-1) {
        this.activeTab = 0;
        this.activeTabData = tabId;
      } else {
        var tabId = this.tabsPreferences.indexOf(tab);
        if(tabId!==-1) {
          this.activeTab = 1;
          this.activeTabPreferences = tabId;
        } else {
          this.activeTab = 0;
          this.activeTabPreferences = 0;
        }
      }
    });
    EventBus.$on('update-module-'+this.id, mod => {
      this.setPropertyFromInputFile(mod);
    });
    this.saveModuleData();
  }
}
</script>

<style scoped>
  #subheader {
    margin: 20px;
    font-size: 30px;
    line-height: 1.5;
  }
  * {
    -moz-box-sizing: border-box;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
  }
  table.v-table tbody td:first-child, table.v-table tbody td:not(:first-child), table.v-table tbody th:first-child, table.v-table tbody th:not(:first-child), table.v-table thead td:first-child, table.v-table thead td:not(:first-child), table.v-table thead th:first-child, table.v-table thead th:not(:first-child) {
    padding: 0 10px
  }
  table.v-table thead th {
    background-color: #f2f2f2;
    max-width: 150px;
    white-space: initial;
    word-wrap: break-word;
    color: #333333 !important;
    font-size: 13px;
    font-weight: bold;
    border-left: 1px dotted LightGray;
    border-right: 1px dotted LightGray;
    border-bottom: 1px solid LightGray;
  }
  table.v-table tbody td {
    font-family: 'Roboto Mono', sans-serif !important;
    word-wrap: break-word;
    font-size: 12px;
    font-weight: normal;
    border-left: 1px dotted LightGray;
    border-right: 1px dotted LightGray;
  }
  .v-input {
    font-size: 12px;
  }
  .v-text-field {
    padding-top: 0px;
    margin-top: 0px;
  }
  .column-wrap {
    width: 1px;
    white-space: nowrap;
  }
</style>



// WEBPACK FOOTER //
// src/components/Workspace/Methods/CATSDModal.vue